/**
 * Theme metadata.
 *
 * The actual CSS variables live in src/index.css under [data-theme="…"]
 * selectors. This module only carries display metadata (name, swatch) plus
 * the storage/apply helpers so the inline boot script in index.html and the
 * React app stay in sync.
 *
 * To add a new theme: define its CSS variable block in index.css, then add
 * an entry below and to `themeOrder`. See MASTERPLAN.md §13 (Themes).
 */

export type ThemeId = "dark" | "light";

export interface ThemeMeta {
  id: ThemeId;
  name: string;
  /** Representative background — fills the switcher dot so dark vs light reads at a glance. */
  swatch: string;
}

export const themes: Record<ThemeId, ThemeMeta> = {
  dark: { id: "dark", name: "Graphite", swatch: "#16181b" },
  light: { id: "light", name: "Paper", swatch: "#f2efe8" },
};

export const themeOrder: ThemeId[] = ["dark", "light"];

export const THEME_STORAGE_KEY = "maestro.theme";
export const DEFAULT_THEME: ThemeId = "dark";

/** Read the persisted theme choice or fall back. */
export function getInitialTheme(): ThemeId {
  if (typeof window === "undefined") return DEFAULT_THEME;
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored && stored in themes) {
    return stored as ThemeId;
  }
  return DEFAULT_THEME;
}

/** Apply a theme by toggling the html attribute and persisting the choice. */
export function applyTheme(themeId: ThemeId): void {
  if (typeof document === "undefined") return;
  if (!(themeId in themes)) return;
  document.documentElement.setAttribute("data-theme", themeId);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(THEME_STORAGE_KEY, themeId);
  }
}
